import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAIDrawer } from '@/hooks/useAIDrawer';
import useWindowWidth from '@/hooks/useWindowWidth';
import {
  claimChatSurfaceAnchor,
  getChatAppContextInputs,
  releaseChatSurfaceAnchorClaim,
  setChatViewing,
} from '@/chatContext/chatAppContextStore';
import { chatThreadKey } from '@/chatContext/chatThreadKey';
import { beginComposerDraftHandoff } from '@/chatContext/composerDraftHandoff';
import type { ChatRootState } from '@/slices/chat/chatSelectors';
import {
  isDraftConversationId,
  threadHistorySelectionStarted,
  threadNewRequested,
  threadResolutionFinished,
} from '@/slices/chat/chatSlice';
import {
  ChatDocumentViewerContext,
  isSameDocumentViewerSource,
  type ChatDocumentSidePane,
  type ChatDocumentViewerContextValue,
  type ChatDocumentViewerCurrent,
  type DocumentHighlightRequest,
  type DocumentViewerOpenOptions,
  type DocumentViewerSource,
} from './ChatDocumentViewerContext';
import type { AIDrawerLayout, CompanionShellMode } from './AIDrawerContext';
import {
  LECTURE_DOCUMENT_SESSION_PATH,
  readLectureDocumentSessionRequest,
  withoutLectureDocumentSessionRequest,
} from './lectureDocumentSessionRequest';

interface PreviousCompanionState {
  layout: AIDrawerLayout;
  shellMode: CompanionShellMode;
  wasOpen: boolean;
}

/** The thread the companion was on when a lecture document session began. */
interface PreviousThread {
  key: string;
  conversationId: string | null;
  /** New Chat was waiting for its first turn on that thread. */
  isNew: boolean;
}

let restoredThreadCount = 0;

/**
 * New Chat is still waiting for its first turn on the thread it was asked
 * for. Once a turn has been sent the conversation is the student's, even
 * though `newThreadKey` still names that thread.
 */
const isAwaitingFirstTurn = (chat: ChatRootState['chat']): boolean =>
  chat.newThreadKey === chat.threadTargetKey &&
  (chat.activeConversationId === null ||
    isDraftConversationId(chat.activeConversationId));

/** Focus goes back to a control still on screen and reachable. */
const focusIfReachable = (element: HTMLElement | null) => {
  if (!element?.isConnected || element.closest('[inert]')) return;
  element.focus({ preventScroll: true });
};

interface OpenDocument extends ChatDocumentViewerCurrent {
  /**
   * Shown in the floating shell's expanded view (desktop web, over a window
   * rather than on the bare AI desktop). There the document only exists in
   * that view, so leaving it closes the document.
   */
  isInShell: boolean;
}

/**
 * A new chat's first send gives it a client draft id, and `turn.start` re-keys
 * that draft to the server's id (a failed first turn re-keys it back). Neither
 * is the student moving to another conversation. A re-key removes the id it
 * came from, which is what tells it apart from choosing a thread in history.
 */
export const isSameConversationRekey = (
  previous: string | null,
  next: string | null,
  conversations: Record<string, unknown>
): boolean => {
  if (next === null) return false;
  if (previous === null) return isDraftConversationId(next);

  return (
    (isDraftConversationId(previous) || isDraftConversationId(next)) &&
    !(previous in conversations)
  );
};

const ChatDocumentViewerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [current, setCurrent] = useState<OpenDocument | null>(null);
  const requestId = useRef(0);
  const currentRef = useRef<OpenDocument | null>(null);
  const previousCompanion = useRef<PreviousCompanionState | null>(null);
  /**
   * Whether the phone sheet was open before a document opened it: false
   * when the document opened it (and closing puts it away again), null when
   * the document did not have to.
   */
  const sheetWasOpen = useRef<boolean | null>(null);
  /** The thread a lecture document session took over, to go back to. */
  const previousThread = useRef<PreviousThread | null>(null);
  /** The control that opened the session, for focus to go back to. */
  const sessionOpener = useRef<HTMLElement | null>(null);
  /** Set by the student's own close: focus returns once the view is gone. */
  const pendingFocusReturn = useRef<HTMLElement | null>(null);
  /**
   * A session closed by the student choosing another conversation, whose
   * claim is released once that choice has loaded.
   */
  const releasesAfterChoice = useRef(false);
  const location = useLocation();
  const { pathname, search, hash } = location;
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();

  const {
    layout,
    shellMode,
    isOpen,
    setShellMode,
    open: openCompanion,
    close: closeCompanion,
  } = useAIDrawer();

  const store = useStore<ChatRootState>();
  // The bare AI desktop at `/` is the full-page chat (the companion stands
  // down there — see AIDrawerRenderer), so a document opened from it splits
  // the desktop itself. Everywhere else on desktop web the chat is the
  // floating shell (or the study drawer, which hands off to it). Below 900px
  // there is no desktop and `/` forwards to the Dashboard; the companion is
  // the full-screen sheet there, and on native.
  const isDesktopWeb = windowWidth > 900 && !Capacitor.isNativePlatform();
  const opensOnDesktop = isDesktopWeb && pathname === '/';
  const usesDesktopCompanion = isDesktopWeb && pathname !== '/';

  /**
   * Make an already loaded conversation the thread for `key`, without a
   * re-read: the resolver sees its target reached and leaves it alone.
   */
  const fileLoadedConversation = useCallback(
    (key: string, conversationId: string) => {
      const resolutionId = `document-session-return-${++restoredThreadCount}`;

      store.dispatch(
        threadHistorySelectionStarted({ key, resolutionId, conversationId })
      );

      store.dispatch(threadResolutionFinished({ key, resolutionId }));
    },
    [store]
  );

  /**
   * Keep the conversation the student chose over the session's own: release
   * the lecture claim and file that conversation (or their New Chat) under
   * the page's thread key, so the resolver does not replace it with the
   * page's thread.
   */
  const releaseKeepingConversation = useCallback(() => {
    releaseChatSurfaceAnchorClaim();

    const pageKey = chatThreadKey(getChatAppContextInputs());
    const chat = store.getState().chat;
    if (!chat.threadTargetKey || chat.threadTargetKey === pageKey) return;

    if (isAwaitingFirstTurn(chat)) {
      store.dispatch(threadNewRequested(pageKey));
      return;
    }

    if (chat.activeConversationId) {
      fileLoadedConversation(pageKey, chat.activeConversationId);
    }
  }, [fileLoadedConversation, store]);

  /**
   * Hand the companion back to the page and release the lecture claim.
   *
   * `restore` (the document closed) puts back the thread the page was on when
   * that is still the page's thread: a history pick, or a New Chat still
   * waiting for its first turn, would otherwise be lost to a fresh lookup.
   * Anything else resolves as usual. `keep` (the student chose another
   * conversation) keeps their choice, once it has loaded — releasing earlier
   * would send the resolver after the page's thread and drop the choice.
   */
  const endSession = useCallback(
    (mode: 'restore' | 'keep') => {
      const previous = previousThread.current;
      previousThread.current = null;
      sessionOpener.current = null;

      if (mode === 'keep') {
        releasesAfterChoice.current = store.getState().chat.isThreadResolving;
        if (!releasesAfterChoice.current) releaseKeepingConversation();
        return;
      }

      releasesAfterChoice.current = false;
      releaseChatSurfaceAnchorClaim();
      if (!previous) return;

      const pageKey = chatThreadKey(getChatAppContextInputs());
      const chat = store.getState().chat;
      if (pageKey !== previous.key || chat.threadTargetKey === pageKey) return;

      if (previous.isNew) {
        store.dispatch(threadNewRequested(pageKey));
        return;
      }

      const { conversationId } = previous;

      if (
        conversationId &&
        !isDraftConversationId(conversationId) &&
        conversationId in chat.conversations
      ) {
        fileLoadedConversation(pageKey, conversationId);
      }
    },
    [fileLoadedConversation, releaseKeepingConversation, store]
  );

  const clear = useCallback(
    (mode: 'restore' | 'keep' = 'restore') => {
      const wasSession = Boolean(currentRef.current?.sessionLectureId);
      currentRef.current = null;
      setCurrent(null);
      setChatViewing(null);
      if (wasSession) endSession(mode);
    },
    [endSession]
  );

  /**
   * Forget the mode to come back to. Leaving a study drawer's hand-off swaps
   * the shell back for the drawer, which remounts the composer.
   */
  const takePreviousCompanion = useCallback(() => {
    const previous = previousCompanion.current;
    previousCompanion.current = null;
    if (previous?.layout === 'drawer') beginComposerDraftHandoff();
    return previous;
  }, []);

  const show = useCallback(
    (
      source: DocumentViewerSource,
      options: DocumentViewerOpenOptions | undefined,
      sessionLectureId: string | null | 'keep'
    ) => {
      const regions = options?.regions ?? [];
      const page = options?.page ?? regions[0]?.page ?? null;
      const exam = options?.exam ?? null;
      const sidePane = options?.sidePane ?? 'chat';

      // Set (or cleared) on every request, the same document's included.
      const requestedHighlight: DocumentHighlightRequest | undefined =
        regions.length > 0 || options?.highlightText
          ? {
              page,
              factIds: options?.factIds ?? [],
              regions,
              ...(options?.highlightText
                ? { text: options.highlightText }
                : {}),
            }
          : undefined;

      const existing = currentRef.current;
      const entersShell = !existing && usesDesktopCompanion;
      const opensSheet = !existing && !isDesktopWeb;

      const session =
        sessionLectureId === 'keep'
          ? (existing?.sessionLectureId ?? null)
          : sessionLectureId;

      const next: OpenDocument =
        existing && isSameDocumentViewerSource(existing.source, source)
          ? {
              ...existing,
              requestedPage: page,
              requestedHighlight,
              requestId: ++requestId.current,
              sessionLectureId: session,
              lectureExam: exam ?? existing.lectureExam ?? null,
              sidePane,
            }
          : {
              source,
              requestedPage: page,
              requestedHighlight,
              requestId: ++requestId.current,
              host:
                existing?.host ?? (opensOnDesktop ? 'desktop' : 'companion'),
              isInShell: existing?.isInShell ?? usesDesktopCompanion,
              sessionLectureId: session,
              lectureExam: exam,
              sidePane,
            };

      const startsSession =
        session !== null && session !== existing?.sessionLectureId;

      if (startsSession) {
        releasesAfterChoice.current = false;

        // Only the first session keeps the thread to go back to; a second
        // lecture opened over it returns to the same page thread.
        if (!existing?.sessionLectureId) {
          const chat = store.getState().chat;

          previousThread.current =
            chat.threadTargetKey && !chat.isThreadResolving
              ? {
                  key: chat.threadTargetKey,
                  conversationId: chat.activeConversationId,
                  isNew: isAwaitingFirstTurn(chat),
                }
              : null;

          const opener = document.activeElement;

          sessionOpener.current =
            opener instanceof HTMLElement && opener !== document.body
              ? opener
              : null;
        }

        // The lecture is the surface anchor from the moment the session opens
        // — not once the file has loaded — so the companion is on the
        // lecture's thread (resolving, then ready) before the student can
        // send anything, and a turn sent while the PDF still downloads or
        // processes stays in that thread. Pages and the file's name follow
        // from the viewer. The claim holds in every viewer state, locked and
        // failed included: the server grounds a lecture only after its own
        // access check, and releasing on a late locked or error report would
        // move the conversation away under a turn already sent in it.
        claimChatSurfaceAnchor({ type: 'lecture', lectureId: session });
      }

      const apply = () => {
        if (entersShell) {
          previousCompanion.current = { layout, shellMode, wasOpen: isOpen };
          setShellMode('expanded');
        }

        // On a phone (and native) the document shows in the companion's
        // sheet. A trigger inside the chat finds it open; View Document and
        // the study panel open it, and closing the document puts it away.
        if (opensSheet) {
          sheetWasOpen.current = isOpen;
          if (!isOpen) openCompanion();
        }

        currentRef.current = next;
        setCurrent(next);
      };

      if (entersShell && layout === 'drawer') {
        // The study drawer hands the conversation to the lazily loaded shell.
        // As a transition the drawer stays on screen until the shell is ready,
        // rather than a loading placeholder flashing in between.
        beginComposerDraftHandoff();
        startTransition(apply);
        return;
      }

      apply();
    },
    [
      isDesktopWeb,
      isOpen,
      layout,
      openCompanion,
      opensOnDesktop,
      setShellMode,
      shellMode,
      store,
      usesDesktopCompanion,
    ]
  );

  const open = useCallback(
    (source: DocumentViewerSource, options?: DocumentViewerOpenOptions) =>
      show(source, options, 'keep'),
    [show]
  );

  const openLectureSession = useCallback(
    (lectureId: string, options?: DocumentViewerOpenOptions) =>
      show({ kind: 'lecture', lectureId }, options, lectureId),
    [show]
  );

  const closeWith = useCallback(
    (mode: 'restore' | 'keep') => {
      clear(mode);

      // A sheet the document opened goes away with it — unless the student
      // has just chosen another conversation in it.
      const sheet = sheetWasOpen.current;
      sheetWasOpen.current = null;
      if (sheet === false && mode === 'restore') closeCompanion();

      const previous = takePreviousCompanion();
      if (!previous) return;

      // The student chose a conversation in the document view: it stays on
      // screen, so a companion that was put away before comes back as the
      // window rather than the bubble.
      const keepsChatOpen = mode === 'keep';

      if (previous.layout === 'shell') {
        setShellMode(
          keepsChatOpen && previous.shellMode === 'bubble'
            ? 'window'
            : previous.shellMode
        );

        return;
      }

      setShellMode(previous.wasOpen || keepsChatOpen ? 'window' : 'bubble');
    },
    [clear, closeCompanion, setShellMode, takePreviousCompanion]
  );

  // The student's own close (the toolbar, Escape): focus goes back to the
  // control that opened the session, if nothing else has it by then.
  const close = useCallback(() => {
    const opener = sessionOpener.current;
    closeWith('restore');
    pendingFocusReturn.current = opener;
  }, [closeWith]);

  useEffect(() => {
    if (current) return;

    const opener = pendingFocusReturn.current;
    pendingFocusReturn.current = null;
    const focused = document.activeElement;

    if (focused && focused !== document.body && focused.isConnected) return;
    focusIfReachable(opener);
  }, [current]);

  // The document only renders in the shell's expanded view. Anything else
  // that moves the shell out of it — minimize, the `c` hotkey, Intercom, a
  // navigation out of the chat, an outside click — closes the document first,
  // and the move the student asked for stands. A layout effect, so the split
  // is never painted inside the small window.
  useLayoutEffect(() => {
    if (!current?.isInShell || shellMode === 'expanded') return;

    takePreviousCompanion();
    clear();
  }, [clear, current, shellMode, takePreviousCompanion]);

  // On a phone, putting the sheet away ends a document that opened it, and a
  // lecture document session in any case: the page's own thread is what the
  // companion shows the next time it opens.
  useEffect(() => {
    if (!current || current.isInShell || current.host !== 'companion') return;
    if (isDesktopWeb || isOpen) return;
    if (!current.sessionLectureId && sheetWasOpen.current !== false) return;

    sheetWasOpen.current = null;
    clear();
  }, [clear, current, isDesktopWeb, isOpen]);

  // A document belongs to the page it was opened on. A window opening over
  // the AI desktop changes the path while the desktop stays mounted beneath
  // it, and closes the desktop's document; coming back to `/` does not reopen
  // it. The drawer-to-shell hand-off does not change the path, so it never
  // lands here.
  const lastPathname = useRef(pathname);

  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (currentRef.current) closeWith('restore');
  }, [closeWith, pathname]);

  // `/document/:lectureId` redirects here with the request in the query. The
  // session starts once the redirect has settled — the navigation has closed
  // whatever was open (the effect above) and the companion has taken its
  // layout for this page, so it opens exactly as View Document would — and
  // the params come off the address without a history entry, so neither Back
  // nor a reload opens it again.
  const openLectureSessionRef = useRef(openLectureSession);
  openLectureSessionRef.current = openLectureSession;
  const locationState = useRef(location.state);
  locationState.current = location.state;

  useEffect(() => {
    if (pathname !== LECTURE_DOCUMENT_SESSION_PATH) return;
    const request = readLectureDocumentSessionRequest(search);
    if (!request) return;

    const timer = window.setTimeout(() => {
      navigate(
        {
          pathname,
          search: withoutLectureDocumentSessionRequest(search),
          hash,
        },
        { replace: true, state: locationState.current }
      );

      openLectureSessionRef.current(
        request.lectureId,
        request.page ? { page: request.page } : undefined
      );
    }, 0);

    return () => window.clearTimeout(timer);
  }, [hash, navigate, pathname, search]);

  // …and to the conversation it was opened from: the student switching
  // threads, New chat or a history pick closes it. A draft becoming its server
  // id does not, and neither does the thread resolver's own lookup — the move
  // a lecture document session makes to the lecture's thread, or a companion
  // opened for the first time finding its page's thread (the phone sheet a
  // document opens). A lookup is told apart by its resolution id, so a pick
  // or New Chat made while one runs still closes the document. Read on every
  // dispatch rather than after a render: the lookup's start, its answer, and
  // its finish arrive as separate actions.
  const closeWithRef = useRef(closeWith);
  closeWithRef.current = closeWith;
  const releaseKeepingConversationRef = useRef(releaseKeepingConversation);
  releaseKeepingConversationRef.current = releaseKeepingConversation;

  useEffect(() => {
    const initial = store.getState().chat;
    let lastConversationId = initial.activeConversationId;
    let lastNewThreadKey = initial.newThreadKey;
    /** The resolver's latest anchor lookup. */
    let lookupResolutionId: string | null = null;

    return store.subscribe(() => {
      const chat = store.getState().chat;
      const next = chat.activeConversationId;

      // New Chat asked for, even over a conversation that was already empty
      // (the session's lookup found nothing, or has not answered yet).
      const isNewChatRequest =
        chat.newThreadKey !== null && chat.newThreadKey !== lastNewThreadKey;

      lastNewThreadKey = chat.newThreadKey;

      // The resolver starting (or retrying) an anchor lookup, which clears
      // the conversation until it answers. A history pick names its
      // conversation as it starts, and New Chat carries no resolution id, so
      // neither is mistaken for one.
      if (
        chat.isThreadResolving &&
        chat.threadResolutionId !== null &&
        next === null
      ) {
        lookupResolutionId = chat.threadResolutionId;
      }

      // Its start and its answer; once it has finished, a move is someone's.
      const isLookupMove =
        chat.isThreadResolving &&
        chat.threadResolutionId !== null &&
        chat.threadResolutionId === lookupResolutionId;

      const previous = lastConversationId;
      const isSwitch = next !== lastConversationId;
      lastConversationId = next;

      const isStudentMove =
        isNewChatRequest ||
        (isSwitch &&
          !isLookupMove &&
          !isSameConversationRekey(previous, next, chat.conversations));

      if (currentRef.current && isStudentMove) {
        closeWithRef.current('keep');
      }

      // The conversation the student chose has loaded: now the claim can go.
      if (
        releasesAfterChoice.current &&
        !store.getState().chat.isThreadResolving
      ) {
        releasesAfterChoice.current = false;
        releaseKeepingConversationRef.current();
      }
    });
  }, [store]);

  // Leaving the protected app (sign-out) unmounts the provider: nothing it
  // opened may keep holding the companion's anchor or grounding.
  useEffect(
    () => () => {
      releaseChatSurfaceAnchorClaim();
      setChatViewing(null);
    },
    []
  );

  // Only the pane beside the document changes: same request, no scroll, no
  // highlight, and nothing about the session or the companion's mode.
  const setSidePane = useCallback((pane: ChatDocumentSidePane) => {
    const existing = currentRef.current;
    if (!existing || existing.sidePane === pane) return;

    const next = { ...existing, sidePane: pane };
    currentRef.current = next;
    setCurrent(next);
  }, []);

  const value = useMemo<ChatDocumentViewerContextValue>(
    () => ({ current, open, openLectureSession, close, setSidePane }),
    [close, current, open, openLectureSession, setSidePane]
  );

  return (
    <ChatDocumentViewerContext.Provider value={value}>
      {children}
    </ChatDocumentViewerContext.Provider>
  );
};

export default ChatDocumentViewerProvider;
